import React from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, Combobox, Button, DateInput, TextArea } from '@origo/origo-ui';
import type { RelationFormValues, SelectOption } from '../../../types';
import styles from './RelationEditDialog.module.css';

export interface RelationEditDialogOptions {
    motpart: SelectOption[];
    type: SelectOption[];
}

export type RelationDialogMode = 'create' | 'edit';

export type RelationFieldErrors = Partial<Record<'motpartPlanId' | 'type', string>>;

interface RelationEditDialogProps {
    open: boolean;
    mode: RelationDialogMode;
    values: RelationFormValues;
    options: RelationEditDialogOptions;
    fieldErrors?: RelationFieldErrors;
    onChange: (field: keyof RelationFormValues, value: string) => void;
    onClose: () => void;
    onSave: () => void;
    isSaving?: boolean;
}

function toComboValue(value: string): string[] {
    return value ? [value] : [];
}

const RelationEditDialog: React.FC<RelationEditDialogProps> = ({
    open,
    mode,
    values,
    options,
    fieldErrors = {},
    onChange,
    onClose,
    onSave,
    isSaving = false,
}) => {
    const { t } = useTranslation();

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={mode === 'create' ? t('relations.dialog.create') : t('relations.dialog.edit')}
            footer={
                <div className={styles.footer}>
                    <Button variant="secondary" onClick={onClose} disabled={isSaving}>
                        {t('common.cancel')}
                    </Button>
                    <Button variant="primary" onClick={onSave} disabled={isSaving}>
                        {t('common.save')}
                    </Button>
                </div>
            }
        >
            <div className={styles.fields}>
                <Combobox
                    label={t('relations.fields.tilArealplan')}
                    placeholder={t('relations.fields.tilArealplanPlaceholder')}
                    value={toComboValue(values.motpartPlanId)}
                    onChange={selected => onChange('motpartPlanId', selected[0] ?? '')}
                    options={options.motpart}
                    error={fieldErrors.motpartPlanId}
                />
                <Combobox
                    label={t('relations.fields.type')}
                    value={toComboValue(values.type)}
                    onChange={selected => onChange('type', selected[0] ?? '')}
                    options={options.type}
                    error={fieldErrors.type}
                />
                <DateInput
                    label={t('relations.fields.vedtakEndeligPlanDato')}
                    value={values.vedtakEndeligPlanDato}
                    onChange={e => onChange('vedtakEndeligPlanDato', e.target.value)}
                />
                <TextArea
                    label={t('relations.fields.begrunnelse')}
                    value={values.begrunnelse}
                    onChange={e => onChange('begrunnelse', e.target.value)}
                    rows={3}
                />
            </div>
        </Dialog>
    );
};

export default RelationEditDialog;
